import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
interface DraggableDesignProps {
  designId: string;
  imageUrl: string;
  scale: number;
  initialX?: number;
  initialY?: number;
  isSelected: boolean;
  zIndex: number;
  onSelect: (id: string) => void;
  onPositionChange: (x: number, y: number) => void;
  containerBounds: {
    width: number;
    height: number;
  };
}
export function DraggableDesign({
  designId,
  imageUrl,
  scale,
  initialX = 0,
  initialY = 0,
  isSelected,
  zIndex,
  onSelect,
  onPositionChange,
  containerBounds
}: DraggableDesignProps) {
  const [position, setPosition] = useState({
    x: initialX,
    y: initialY
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [designSize, setDesignSize] = useState({
    width: 0,
    height: 0
  });
  const dragStartPos = useRef({
    x: 0,
    y: 0
  });
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    setPosition({
      x: initialX,
      y: initialY
    });
  }, [initialX, initialY]);
  // Get design image dimensions
  useEffect(() => {
    if (imageRef.current) {
      const updateSize = () => {
        if (imageRef.current) {
          setDesignSize({
            width: imageRef.current.offsetWidth,
            height: imageRef.current.offsetHeight
          });
        }
      };
      if (imageRef.current.complete) {
        updateSize();
      } else {
        imageRef.current.addEventListener('load', updateSize);
      }
      return () => {
        if (imageRef.current) {
          imageRef.current.removeEventListener('load', updateSize);
        }
      };
    }
  }, [imageUrl, scale]);
  const clampPosition = (x: number, y: number) => {
    if (!containerBounds.width || !containerBounds.height || !designSize.width || !designSize.height) {
      return {
        x,
        y
      };
    }
    // Calculate the scaled design dimensions
    const scaledWidth = designSize.width * scale;
    const scaledHeight = designSize.height * scale;
    // Calculate boundaries (designs are centered, so we need to account for half their size)
    const maxX = containerBounds.width / 2 - scaledWidth / 2;
    const minX = -containerBounds.width / 2 + scaledWidth / 2;
    const maxY = containerBounds.height / 2 - scaledHeight / 2;
    const minY = -containerBounds.height / 2 + scaledHeight / 2;
    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y))
    };
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(designId);
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStartPos.current.x;
        const newY = e.clientY - dragStartPos.current.y;
        const clampedPos = clampPosition(newX, newY);
        setPosition(clampedPos);
        onPositionChange(clampedPos.x, clampedPos.y);
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onPositionChange, containerBounds, designSize, scale]);
  // Only show border when actively hovered or selected
  const showFrame = (isHovered || isSelected) && !isDragging;
  return <motion.div className={`absolute top-1/2 left-1/2 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} style={{
    x: position.x,
    y: position.y,
    scale,
    zIndex: 100 + zIndex
  }} onMouseDown={handleMouseDown} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} drag dragMomentum={false} dragConstraints={{
    left: -containerBounds.width / 2 + designSize.width * scale / 2,
    right: containerBounds.width / 2 - designSize.width * scale / 2,
    top: -containerBounds.height / 2 + designSize.height * scale / 2,
    bottom: containerBounds.height / 2 - designSize.height * scale / 2
  }} onDragEnd={(_, info) => {
    const newX = position.x + info.offset.x;
    const newY = position.y + info.offset.y;
    const clampedPos = clampPosition(newX, newY);
    setPosition(clampedPos);
    onPositionChange(clampedPos.x, clampedPos.y);
  }} data-id="element-309">
      <img ref={imageRef} src={imageUrl} alt="Design" className="max-w-[200px] pointer-events-none" draggable={false} data-id="element-310" />

      {/* Only show border when hovered or selected (not while dragging) */}
      {showFrame && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className={`absolute inset-0 border-2 rounded pointer-events-none ${isSelected ? 'border-blue-500 border-solid' : 'border-dashed border-blue-400'}`} data-id="element-311" />}
    </motion.div>;
}